import { useQueryClient } from "@tanstack/react-query";
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import { ApiStreamQuestionRequest } from "../api";
import { QUERY_KEYS, ROUTES } from "../constants";
import { useCreateChat } from "../hooks/mutations/useCreateChat";
import { useChatMessages, useSubmitFeedback } from "../hooks/queries/useChatMessages";
import { useChatMetadata } from "../hooks/queries/useChatMetadata";
import { useStreamingQuestion } from "../hooks/queries/useStreamingQuestion";
import { AnswerFeedbackScore, ChatMessage, ChatMetadata, PreDefinedConstraints } from "../types";

// Stream State Interface
export interface StreamState {
  chatId: string;
  question: string;
  isStreaming: boolean;
  isPaused: boolean;
  todoItems: { message: string; agentName: string }[];
  abortController: AbortController;
  preSetConstraints?: PreDefinedConstraints;
}

// Agent Message Helper
export const getAgentMessage = (agentName: string): string => {
  const agentMessages = {
    MaiteAgent: ["Getting Started", "Understanding your question"],
    MMMAgent: [
      "Extracting relevant Marketing performance data",
      "Looking at your Marketing Performance",
    ],
    WarcExpertAgent: ["Searching relevant industry insights, case studies and more"],
    SynthesisAgent: ["Formulating a response", "Thinking, Analysing and formulating a response."],
  };

  const messages = agentMessages[agentName as keyof typeof agentMessages];
  return messages[Math.floor(Math.random() * messages.length)] || "Processing...";
};

// Define the context type
interface ChatContextType {
  // Chat messages and loading state
  chatMessages: ChatMessage[];
  isLoadingMessages: boolean;
  messagesError: any;

  // Metadata
  metadata: ChatMetadata | undefined;
  isLoadingMetadata: boolean;
  metadataError: any;

  // UI state
  currentQuestion: string | undefined;
  currentPreSetConstraints: PreDefinedConstraints | undefined;
  enabledDocumentIds: string[];
  setEnabledDocumentIds: React.Dispatch<React.SetStateAction<string[]>>;
  selectedModel: "CLAUDE" | "GEMINI" | undefined;
  setSelectedModel: React.Dispatch<React.SetStateAction<"CLAUDE" | "GEMINI" | undefined>>;

  // Streaming state
  isStreaming: boolean;
  todoItems: string[];

  // Actions
  askQuestion: (
    question: string,
    warcEnabled: boolean,
    constraints?: PreDefinedConstraints
  ) => Promise<any>;
  scoreAnswer: (messageId: string, score: AnswerFeedbackScore) => Promise<any> | undefined;
  clearChat: () => void;

  // Loading states
  isAsking: boolean;
  isScoring: boolean;

  setCurrentStreamId: (chatId: string) => void;

  activeStreams: Record<string, StreamState>;
}

// Create the context
const ChatContext = createContext<ChatContextType | null>(null);

// Provider component
export const ChatContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { streamQuestion } = useStreamingQuestion();

  // Stream management state
  const [activeStreams, setActiveStreams] = useState<Record<string, StreamState>>({});
  const [currentStreamId, setCurrentStreamId] = useState<string | null>(null);

  const currentStream = currentStreamId ? activeStreams[currentStreamId] : null;

  // In your ChatContext component:
  const location = useLocation();

  // Extract chatId from the current URL using regex - now reactive
  const chatId = (() => {
    const match = location.pathname.match(/\/maite-v2\/([^/]+)/);
    return match ? match[1] : undefined;
  })();

  const isStreaming = currentStream?.isStreaming || false;
  const currentQuestion = currentStream?.question;
  const todoItems = currentStream?.todoItems || [];
  const currentPreSetConstraints = currentStream?.preSetConstraints;

  const createChatMutation = useCreateChat();

  // UI state
  const [enabledDocumentIds, setEnabledDocumentIds] = useState<string[]>([]);
  const [selectedModel, setSelectedModel] = useState<"CLAUDE" | "GEMINI">();
  const enabledDocumentIdsRef = useRef<string[]>([]);

  // React Query hooks
  const chatMessages = useChatMessages(chatId);
  const chatMetadata = useChatMetadata();
  const submitFeedbackMutation = useSubmitFeedback();

  // Set default model when metadata loads (exclude GEMINI as it's disabled)
  useEffect(() => {
    if (chatMetadata.data?.availableModels.length && !selectedModel) {
      const enabledModels = chatMetadata.data.availableModels.filter((model) => model !== "GEMINI");
      if (enabledModels.length) {
        setSelectedModel(enabledModels[0] as "CLAUDE" | "GEMINI");
      }
    }
  }, [chatMetadata.data?.availableModels, selectedModel]);

  useEffect(() => {
    enabledDocumentIdsRef.current = enabledDocumentIds;
  }, [enabledDocumentIds]);

  // Stream management functions
  const updateStreamTodoItems = useCallback(
    (chatId: string, data: { message: string; agentName: string }) => {
      setActiveStreams((prev) => {
        const stream = prev[chatId];
        if (!stream) return prev;

        const updatedTodoItems = [...stream.todoItems];
        const existingIndex = updatedTodoItems.findIndex(
          (item) => item.agentName === data.agentName
        );

        if (existingIndex >= 0) {
          // Update existing agent's message
          updatedTodoItems[existingIndex] = {
            message: getAgentMessage(data.agentName),
            agentName: data.agentName,
          };
        } else {
          // Add new agent
          updatedTodoItems.push({
            message: getAgentMessage(data.agentName),
            agentName: data.agentName,
          });
        }

        return {
          ...prev,
          [chatId]: {
            ...stream,
            todoItems: updatedTodoItems,
            lastActivity: new Date(),
          },
        };
      });
    },
    []
  );

  const updateStreamConstraints = useCallback(
    (chatId: string, preSetConstraints?: PreDefinedConstraints) => {
      setActiveStreams((prev) => {
        const stream = prev[chatId];
        if (!stream) return prev;

        return {
          ...prev,
          [chatId]: {
            ...stream,
            preSetConstraints,
          },
        };
      });
    },
    []
  );

  const completeStream = useCallback(
    async (chatId: string, result: any) => {
      setActiveStreams((prev) => {
        // Add the user message and assistant message to React Query cache
        const targetChatId = result.chatId || chatId;
        const currentStream = prev[targetChatId];

        if (targetChatId && currentStream) {
          queryClient.setQueryData(
            QUERY_KEYS.chatMessages(targetChatId),
            (oldData: ChatMessage[] = []) => [
              ...oldData,
              // User message with preset constraints
              {
                user: "user",
                content: currentStream.question,
                preSetConstraints: result.preSetConstraints || currentStream.preSetConstraints,
              } as ChatMessage,
              // Assistant message with final result
              result.finalMessage,
            ]
          );

          // Mark messages as stale but don't refetch immediately
          // This gives the server time to persist the message
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.chatMessages(targetChatId),
          });

          // Invalidate chat history to refresh the sidebar
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.chatHistory(),
          });

          // Invalidate metadata to update remaining query count
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.chatMetadata(),
          });
        }

        // Remove the stream after processing
        const { [chatId]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [queryClient]
  );

  const handleStreamError = useCallback(
    (chatId: string, result: any) => {
      console.error(`Stream error for chat ${chatId}:`, result);

      toast.error("An error occurred while processing your question");

      setActiveStreams((prev) => {
        // Use result.chatId like completeStream does (important for consistency!)
        const targetChatId = result.chatId || chatId;
        const currentStream = prev[targetChatId];

        if (!currentStream) {
          console.warn("No stream found for chatId:", targetChatId);
          return prev;
        }

        // Add messages to chat (same pattern as completeStream)
        if (targetChatId && currentStream) {
          currentStream.todoItems = [];
          queryClient.setQueryData(
            QUERY_KEYS.chatMessages(targetChatId),
            (oldData: ChatMessage[] = []) => {
              return [
                ...oldData,
                // User message with preset constraints
                {
                  user: "user",
                  content: currentStream.question,
                  preSetConstraints: currentStream.preSetConstraints,
                } as ChatMessage,
                // Error message from assistant
                result.finalMessage,
              ];
            }
          );

          // Invalidate to refresh from server
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.chatMessages(targetChatId),
          });

          // Invalidate chat history to refresh the sidebar
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.chatHistory(),
          });

          // Invalidate metadata to update remaining query count
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.chatMetadata(),
          });
        }
        // Remove the stream after processing (same as completeStream)
        const { [targetChatId]: _removed, ...rest } = prev;
        return rest;
      });
    },
    [queryClient]
  );

  const startStream = useCallback(
    async (chatId: string, question: string, requestData: ApiStreamQuestionRequest) => {
      // Create new stream state
      const streamState: StreamState = {
        chatId,
        question,
        isStreaming: true,
        isPaused: false,
        todoItems: [{ message: getAgentMessage("MaiteAgent"), agentName: "MaiteAgent" }],
        abortController: new AbortController(),
      };

      setActiveStreams((prev) => {
        return { ...prev, [chatId]: streamState };
      });
      setCurrentStreamId(chatId);

      // Start the actual streaming
      return streamQuestion(requestData, streamState.abortController.signal, {
        onAgentUpdate: (data) => {
          if (data.preSetConstraints) {
            updateStreamConstraints(chatId, data.preSetConstraints);
          }
        },
        onAgentStep: (data) => updateStreamTodoItems(chatId, data),
        onFinalResult: (result) => completeStream(chatId, result),
        onError: (error) => handleStreamError(chatId, error),
      });
    },
    [
      streamQuestion,
      updateStreamConstraints,
      updateStreamTodoItems,
      completeStream,
      handleStreamError,
    ]
  );

  const askQuestion = useCallback(
    async (question: string, warcEnabled: boolean, constraints?: PreDefinedConstraints) => {
      try {
        let targetChatId = chatId;

        // Only create a new chat if we don't have an existing chatId
        if (!targetChatId) {
          const chatResult = await createChatMutation.mutateAsync();
          targetChatId = chatResult.data.chatId ?? "";

          // Navigate to the new chat immediately
          history.push(ROUTES.CHAT_WITH_ID(targetChatId));
        }

        const requestDocumentIds = enabledDocumentIdsRef.current;
        const requestData = {
          question,
          chatId: targetChatId,
          documentIds: requestDocumentIds.length > 0 ? [...requestDocumentIds] : undefined,
          model: selectedModel,
          warcEnabled,
          ...(constraints?.startDate && { startDate: constraints.startDate }),
          ...(constraints?.endDate && { endDate: constraints.endDate }),
          ...(constraints?.brand && { brand: constraints.brand }),
          ...(constraints?.category && { category: constraints.category }),
          ...(constraints?.product && { product: constraints.product }),
        };

        // Start new stream (this won't cancel existing streams)
        await startStream(targetChatId, question, requestData);

        // Clear document IDs after successful submission
        setEnabledDocumentIds([]);
      } catch (error) {
        console.error("Failed to stream question:", error);
        toast.error("Failed to send question. Please try again.");
        setEnabledDocumentIds([]);
        throw error;
      }
    },
    [chatId, selectedModel, startStream, history, createChatMutation]
  );

  const scoreAnswer = useCallback(
    (messageId: string, score: AnswerFeedbackScore) => {
      if (!currentStream?.chatId || !selectedModel) return;

      return submitFeedbackMutation.mutateAsync({
        chatId: currentStream.chatId,
        messageId,
        score: score === "positive" ? 1 : -1,
        modelUsed: selectedModel,
      });
    },
    [currentStream?.chatId, selectedModel, submitFeedbackMutation]
  );

  const clearChat = useCallback(() => {
    history.push(ROUTES.CHAT_ROOT);
  }, [history]);

  // Ensure metadata always has values with sensible defaults
  const metadata: ChatMetadata | undefined = chatMetadata.data
    ? chatMetadata.data
    : {
        hendrenTier: "FREE",
        monthlyQueryLimit: 0,
        remainingQueries: 0,
        queryLimitReached: true,
        availableModels: [],
      };

  const contextValue: ChatContextType = {
    // Chat messages and loading state
    chatMessages: chatMessages.data || [],
    isLoadingMessages: chatMessages.isLoading,
    messagesError: chatMessages.error,

    // Metadata
    metadata,
    isLoadingMetadata: chatMetadata.isLoading,
    metadataError: chatMetadata.error,

    // UI state
    currentQuestion,
    currentPreSetConstraints,
    enabledDocumentIds,
    setEnabledDocumentIds,
    selectedModel,
    setSelectedModel,

    // Streaming state - single source of truth
    isStreaming,
    todoItems: todoItems.map((item: any) => item.message),

    // Actions
    askQuestion,
    scoreAnswer,
    clearChat,

    // Loading states
    isAsking: isStreaming,
    isScoring: submitFeedbackMutation.isPending,

    setCurrentStreamId,

    activeStreams,
  };

  return <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>;
};

// Hook to use the chat context
export const useChat = (): ChatContextType => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChat must be used within a ChatContextProvider");
  }
  return context;
};
